import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import { throwIfNotOk } from "@/lib/api-error";
import {
  Plus,
  MagnifyingGlass,
  ArrowCounterClockwise,
  Buildings,
} from "@phosphor-icons/react";
import type { Company } from "@/types";
import {
  CompaniesStats,
  CompaniesTable,
  CompaniesListSkeleton,
  CompanyFormDialog,
} from "@/components/admin/companies";
import type { StatusFilter, CompanyFormSubmit } from "@/components/admin/companies";

export const Route = createFileRoute("/dashboard/admin/companies/")({
  component: AdminCompanies,
});

const TAB_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Aktivne" },
  { value: "suspended", label: "Suspendirane" },
];

function AdminCompanies() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ["admin", "companies"],
    queryFn: async () => {
      const res = await fetch("/api/companies");
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json() as Promise<Company[]>;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CompanyFormSubmit) => {
      const res = await fetch("/api/companies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri kreiranju kompanije");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Company> & { id: string }) => {
      const res = await fetch(`/api/companies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju kompanije");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
      setEditingCompany(null);
    },
  });

  const query = searchQuery.trim().toLowerCase();
  const filtered = companies
    .filter((c) => c.status === statusFilter)
    .filter(
      (c) =>
        !query ||
        c.name.toLowerCase().includes(query) ||
        c.billingEmail.toLowerCase().includes(query),
    );

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Firme
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Firme, njihovi planovi i kreditni saldo.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SegmentedFilter
            ariaLabel="Status firme"
            options={TAB_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <Button onClick={() => setFormOpen(true)} className="shrink-0">
            <Plus className="size-4" />
            Nova firma
          </Button>
        </div>
      </header>

      <CompaniesStats companies={companies} isLoading={isLoading} />

      {/* Search */}
      <div className="flex items-center gap-1 overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm">
        <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
        <Input
          placeholder="Pretraži po nazivu ili emailu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-12 border-0 bg-transparent shadow-none focus-visible:ring-0"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowCounterClockwise className="size-3" />
            Očisti
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <CompaniesListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Buildings}
          title={
            searchQuery
              ? "Nema rezultata pretrage"
              : statusFilter === "active"
                ? "Nema aktivnih firmi"
                : "Nema suspendiranih firmi"
          }
          description={
            searchQuery
              ? "Pokušajte s drugačijim pojmom za pretragu."
              : statusFilter === "active"
                ? "Dodajte prvu firmu klikom na dugme Nova firma."
                : "Trenutno nijedna firma nije suspendirana."
          }
          hasFilters={searchQuery.length > 0}
          onClear={() => setSearchQuery("")}
        />
      ) : (
        <CompaniesTable companies={filtered} onEdit={setEditingCompany} />
      )}

      {/* Create dialog */}
      {formOpen && (
        <CompanyFormDialog
          company={null}
          isPending={createMutation.isPending}
          onSubmit={(data) => createMutation.mutate(data)}
          onClose={() => setFormOpen(false)}
        />
      )}

      {/* Edit dialog */}
      {editingCompany && (
        <CompanyFormDialog
          company={editingCompany}
          isPending={updateMutation.isPending}
          onSubmit={(data) =>
            updateMutation.mutate({ id: editingCompany.id, ...data })
          }
          onClose={() => setEditingCompany(null)}
        />
      )}
    </div>
  );
}
